import { useState, type ReactNode } from 'react';
import { AlertTriangle, ExternalLink, KeyRound, Menu, ShieldCheck, X } from 'lucide-react';
import { Sidebar } from './components/Sidebar';
import { CodePanel, LanguageTabs } from './components/CodeBlock';
import { Sandbox } from './components/Sandbox';
import { portals } from '@riskpay/ui-components';
import {
  ENDPOINTS,
  ERROR_CODES,
  TEST_CARDS,
  TEST_VPAS,
  VERIFICATION_SNIPPETS,
  WEBHOOK_EVENTS,
  type Language,
} from './data/api';

/* ==========================================================================
 *  docs.riskpay.in
 *
 *  Two-column reference layout: prose and schemas on the left, runnable code
 *  and the live sandbox on the right. The right column is sticky on wide
 *  screens and stacks underneath on narrow ones, which is the layout every
 *  developer already has muscle memory for.
 *
 *  The language choice is page-level state: pick Python once and every snippet
 *  on the page follows.
 * ========================================================================== */

function Section({
  id,
  eyebrow,
  title,
  children,
  aside,
}: {
  id: string;
  eyebrow?: string;
  title: string;
  children: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-20 border-b border-line py-12 first:pt-8">
      <div className="grid gap-8 xl:grid-cols-2 xl:gap-12">
        <div className="min-w-0">
          {eyebrow && <p className="eyebrow">{eyebrow}</p>}
          <h2 className="mt-2 text-xl font-semibold tracking-tight">{title}</h2>
          <div className="mt-5 space-y-4 text-sm leading-relaxed text-content-muted">{children}</div>
        </div>
        {aside && <div className="min-w-0 space-y-4 xl:sticky xl:top-20 xl:self-start">{aside}</div>}
      </div>
    </section>
  );
}

function MethodPill({ method }: { method: string }) {
  const tone =
    method === 'GET' ? 'badge-info' : method === 'DELETE' ? 'badge-danger' : 'badge-success';
  return <span className={`badge ${tone} font-mono`}>{method}</span>;
}

function Callout({
  tone = 'info',
  icon: Icon = ShieldCheck,
  children,
}: {
  tone?: 'info' | 'warning';
  icon?: typeof ShieldCheck;
  children: ReactNode;
}) {
  return (
    <div
      className={`flex items-start gap-3 rounded-md border px-4 py-3.5 text-xs leading-relaxed ${
        tone === 'warning'
          ? 'border-warning-ring bg-warning-soft text-[#FCD34D]'
          : 'border-line bg-surface-inset text-content-muted'
      }`}
    >
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <div className="space-y-2">{children}</div>
    </div>
  );
}

export function App() {
  const [language, setLanguage] = useState<Language>('curl');
  const [navOpen, setNavOpen] = useState(false);

  return (
    <div className="min-h-screen">
      {/* ---- Mobile header --------------------------------------------- */}
      <header className="sticky top-0 z-40 flex h-14 items-center justify-between border-b border-line bg-surface/90 px-4 backdrop-blur-md lg:hidden">
        <span className="text-sm font-semibold tracking-tight">RiskPay Docs</span>
        <button
          type="button"
          onClick={() => setNavOpen((v) => !v)}
          className="btn btn-secondary h-9 w-9 p-0"
          aria-label={navOpen ? 'Close navigation' : 'Open navigation'}
          aria-expanded={navOpen}
        >
          {navOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
        </button>
      </header>

      {/* ---- Sidebar ---------------------------------------------------- */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-72 border-r border-line bg-surface-raised transition-transform lg:translate-x-0 ${
          navOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <Sidebar onNavigate={() => setNavOpen(false)} />
      </aside>
      {navOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={() => setNavOpen(false)}
          className="fixed inset-0 z-40 bg-black/60 lg:hidden"
        />
      )}

      {/* ---- Content ---------------------------------------------------- */}
      <main className="lg:pl-72">
        <div className="mx-auto w-full max-w-[1400px] px-5 sm:px-8">
          {/* ---------------------------------------------------------- */}
          <Section
            id="introduction"
            eyebrow="Getting started"
            title="The RiskPay API"
            aside={
              <>
                <CodePanel
                  title="Base URL"
                  source={`# Live
https://api.riskpay.in/v1

# Everything is scoped by the key you send, not by the host:
# an sk_test_ key against this URL operates in test mode.`}
                />
                <Callout>
                  <p>
                    Every response carries an <code className="font-mono text-content">X-Request-Id</code> header. Quote
                    it in any support ticket and we can pull the full server-side trace for that exact call.
                  </p>
                </Callout>
              </>
            }
          >
            <p>
              RiskPay is a RESTful, JSON-over-HTTPS API for accepting and disbursing payments in India. It covers UPI and
              UPI Intent, credit and debit cards, net banking and wallets on the pay-in side, and IMPS, NEFT, RTGS and
              UPI on the payout side.
            </p>
            <p>
              There is one integration and one reconciliation feed regardless of how many rails you enable. Resources are
              addressed by opaque prefixed ids — <code className="font-mono text-content">pay_</code>,{' '}
              <code className="font-mono text-content">rfnd_</code>, <code className="font-mono text-content">pout_</code>
              , <code className="font-mono text-content">setl_</code> — which are never sequential and never guessable.
            </p>
            <p>
              Test and live are separate namespaces sharing one API surface. An id created with a test key does not exist
              for a live key, and vice versa.
            </p>
          </Section>

          {/* ---------------------------------------------------------- */}
          <Section
            id="authentication"
            eyebrow="Getting started"
            title="Authentication"
            aside={
              <>
                <CodePanel
                  title="Authorization header"
                  source={`curl https://api.riskpay.in/v1/payments \\
  -H "Authorization: Bearer sk_live_4f8Ac2Kd91QxZm7Rb3Vt6Yh0"

# The secret key is a bearer credential. Anyone holding it can move money
# on your account, so keep it in a secret manager — never in client-side
# JavaScript, a mobile binary, or version control.`}
                />
                <CodePanel
                  title="401 response"
                  json
                  source={`{
  "error": {
    "type": "authentication_error",
    "code": "invalid_api_key",
    "message": "This API key is invalid or has been revoked.",
    "hint": "Send your secret key as \`Authorization: Bearer sk_live_…\`.",
    "request_id": "c3a91f0e-7b21-4d64-9f88-0e4b2a1c7d55"
  }
}`}
                />
              </>
            }
          >
            <p>
              Every request authenticates with a secret key sent as a bearer token. Keys come in pairs, and each pair is
              bound to a single mode.
            </p>

            <div className="panel overflow-hidden">
              <table className="table-riskpay">
                <thead>
                  <tr>
                    <th>Key</th>
                    <th>Where it belongs</th>
                    <th>Can it move money?</th>
                  </tr>
                </thead>
                <tbody className="numeral">
                  <tr>
                    <td>pk_test_…</td>
                    <td className="font-sans">Client SDKs, checkout pages</td>
                    <td className="font-sans">No — identification only</td>
                  </tr>
                  <tr>
                    <td>pk_live_…</td>
                    <td className="font-sans">Client SDKs, checkout pages</td>
                    <td className="font-sans">No — identification only</td>
                  </tr>
                  <tr>
                    <td>sk_test_…</td>
                    <td className="font-sans">Your backend, sandbox</td>
                    <td className="font-sans">Simulated money only</td>
                  </tr>
                  <tr>
                    <td>sk_live_…</td>
                    <td className="font-sans">Your backend, production</td>
                    <td className="font-sans">Yes — real funds</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p>
              Secret keys are stored only as a SHA-256 digest, so the raw value is shown exactly once at creation. If you
              lose it, generate a new key and revoke the old one; there is no recovery path, by design.
            </p>

            <Callout tone="warning" icon={KeyRound}>
              <p>
                Live keys unlock only after KYC approval. Until then an <code className="font-mono">sk_live_</code> key
                returns <code className="font-mono">403 kyc_incomplete</code>. Test keys are issued the moment you sign
                up, so engineering never waits on compliance.
              </p>
            </Callout>
          </Section>

          {/* ---------------------------------------------------------- */}
          <Section
            id="idempotency"
            eyebrow="Getting started"
            title="Idempotency"
            aside={
              <CodePanel
                title="Safe retry"
                source={`KEY=$(uuidgen)

# First attempt — times out on your side, but may well have succeeded on ours.
curl -X POST https://api.riskpay.in/v1/payments/create \\
  -H "Authorization: Bearer sk_live_…" \\
  -H "Idempotency-Key: $KEY" \\
  -d '{"amount":1499.50,"currency":"INR","payment_method":"UPI"}'

# Retry with the SAME key and the SAME body.
# You get the original response back, and no second payment is created.
curl -X POST https://api.riskpay.in/v1/payments/create \\
  -H "Authorization: Bearer sk_live_…" \\
  -H "Idempotency-Key: $KEY" \\
  -d '{"amount":1499.50,"currency":"INR","payment_method":"UPI"}'

# Replayed responses carry:  Idempotent-Replay: true`}
              />
            }
          >
            <p>
              Every mutating endpoint requires an <code className="font-mono text-content">Idempotency-Key</code> header.
              Send a fresh UUID for each logical operation, and reuse that same UUID on every retry of it.
            </p>
            <p>A key is scoped to one merchant, one endpoint and one request body. That gives four behaviours:</p>
            <ul className="ml-4 list-disc space-y-2 marker:text-content-faint">
              <li>
                <strong className="font-medium text-content">Same key, same body, first call</strong> — the request
                executes normally.
              </li>
              <li>
                <strong className="font-medium text-content">Same key, same body, already complete</strong> — the
                original response is replayed verbatim with{' '}
                <code className="font-mono text-content">Idempotent-Replay: true</code>.
              </li>
              <li>
                <strong className="font-medium text-content">Same key, still in flight</strong> —{' '}
                <code className="font-mono text-content">409 request_in_progress</code>. Wait and retry.
              </li>
              <li>
                <strong className="font-medium text-content">Same key, different body</strong> —{' '}
                <code className="font-mono text-content">422 idempotency_key_reuse</code>. Two different operations must
                never share a key.
              </li>
            </ul>
            <p>Keys expire 24 hours after first use. After that the same value can be reused for a new operation.</p>
          </Section>

          {/* ---------------------------------------------------------- */}
          <Section
            id="money"
            eyebrow="Getting started"
            title="Amounts and currency"
            aside={
              <>
                <CodePanel
                  title="Sending and reading an amount"
                  source={`// Rupees, up to two decimal places. All three are ₹1,499.50.
{ "amount": 1499.50 }
{ "amount": "1499.50" }
{ "amount": "1499.5" }

// A whole-rupee amount needs no decimals.
{ "amount": 1499 }                // ₹1,499.00

// Reading a response: amounts come back as STRINGS.
const net = payment.net_amount.amount;   // "1464.11"
const shown = payment.net_amount.formatted; // "₹1,464.11"

// If your language turns 1499.50 into 1499.4999999999998,
// send the string. We parse it as text, never as a float.
{ "amount": "1499.50" }`}
                />
                <Callout>
                  <p>
                    The fee shown on a payment is frozen at capture time along with the exact rate applied
                    (<code className="font-mono text-content">applied_mdr_bps</code>). A later pricing change never
                    rewrites history.
                  </p>
                </Callout>
              </>
            }
          >
            <p>
              Amounts are in <strong className="text-content">rupees</strong>, with up to two decimal places. You send{' '}
              <code className="font-mono text-content">1499.50</code> and you read{' '}
              <code className="font-mono text-content">"1499.50"</code>. There is no conversion to do and no zeros to
              count.
            </p>
            <p>
              Requests accept a number or a string; strings are safer, because several languages cannot hold{' '}
              <code className="font-mono text-content">1499.50</code> exactly as a double. Either way we parse the
              value as text and hold it internally as an integer number of paisa, so{' '}
              <code className="font-mono text-content">gross = fee + gst + net</code> reconciles to the last paisa on
              every transaction.
            </p>
            <p>
              Responses always return amounts as decimal <em>strings</em>, never JSON numbers — a gateway that hands
              you an amount you cannot compare for equality is a gateway you cannot reconcile against. Every money
              field also carries a <code className="font-mono text-content">formatted</code> form for display; never
              parse that one back into a calculation.
            </p>
            <p>
              MDR is expressed in basis points: <code className="font-mono text-content">250</code> means 2.50%. Fees
              round HALF-UP, and the net payable is derived as gross minus fees, so a settlement always reconciles back
              to the gross exactly.
            </p>
          </Section>

          {/* ---- API reference ---------------------------------------- */}
          {ENDPOINTS.map((endpoint) => (
            <Section
              key={endpoint.id}
              id={endpoint.id}
              eyebrow="API reference"
              title={endpoint.title}
              aside={
                <>
                  <LanguageTabs snippets={endpoint.snippets} language={language} onLanguageChange={setLanguage} />
                  <CodePanel title="Response · 200" source={endpoint.response} json />
                </>
              }
            >
              <div className="flex flex-wrap items-center gap-2.5">
                <MethodPill method={endpoint.method} />
                <code className="numeral rounded border border-line bg-surface-inset px-2 py-1 text-xs text-content">
                  {endpoint.path}
                </code>
                {endpoint.idempotent && <span className="badge badge-info">Idempotency-Key required</span>}
              </div>

              <p>{endpoint.summary}</p>

              <p className="text-xs text-content-subtle">
                Required scope: <code className="font-mono text-content-muted">{endpoint.scope}</code>
              </p>

              <div className="panel overflow-hidden">
                <table className="table-riskpay">
                  <thead>
                    <tr>
                      <th className="w-40">Parameter</th>
                      <th className="w-32">Type</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {endpoint.request.map((field) => (
                      <tr key={field.name}>
                        <td className="align-top">
                          <span className="numeral text-xs text-content">{field.name}</span>
                          {field.required && (
                            <span className="mt-1 block text-2xs uppercase tracking-wider text-brand-400">
                              Required
                            </span>
                          )}
                        </td>
                        <td className="align-top">
                          <span className="numeral text-xs text-content-subtle">{field.type}</span>
                        </td>
                        <td className="align-top text-xs leading-relaxed text-content-muted">{field.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          ))}

          {/* ---- Sandbox ---------------------------------------------- */}
          <Section
            id="test-cards"
            eyebrow="Sandbox"
            title="Test cards"
            aside={<Sandbox />}
          >
            <p>
              In test mode the outcome of a card payment is decided entirely by the card number, so every branch of your
              checkout is reachable without an acquirer. Any future expiry and any 3-digit CVV work.
            </p>

            <div className="panel overflow-hidden">
              <div className="scrollbar-thin overflow-x-auto">
                <table className="table-riskpay">
                  <thead>
                    <tr>
                      <th>Number</th>
                      <th>Network</th>
                      <th>Outcome</th>
                      <th>Behaviour</th>
                    </tr>
                  </thead>
                  <tbody>
                    {TEST_CARDS.map((card) => (
                      <tr key={card.number}>
                        <td className="numeral whitespace-nowrap text-xs">{card.number}</td>
                        <td className="whitespace-nowrap text-xs text-content-muted">{card.network}</td>
                        <td>
                          <span
                            className={`badge ${
                              card.outcome === 'SUCCESS'
                                ? 'badge-success'
                                : card.outcome === 'REQUIRES_3DS'
                                  ? 'badge-info'
                                  : 'badge-danger'
                            }`}
                          >
                            {card.failureCode ?? card.outcome}
                          </span>
                        </td>
                        <td className="text-xs leading-relaxed text-content-muted">{card.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <Callout tone="warning" icon={AlertTriangle}>
              <p>
                Test cards are inert numbers, not real PANs, but treat them with the same handling discipline anyway —
                logging a card number in test is the habit that logs one in production.
              </p>
            </Callout>
          </Section>

          <Section
            id="test-vpas"
            eyebrow="Sandbox"
            title="Test VPAs"
            aside={
              <CodePanel
                title="Driving a sandbox capture"
                source={`# 1. Create the payment with a test VPA.
curl -X POST https://api.riskpay.in/v1/payments/create \\
  -H "Authorization: Bearer sk_test_…" \\
  -H "Idempotency-Key: $(uuidgen)" \\
  -d '{
    "amount": 1499.50,
    "currency": "INR",
    "payment_method": "UPI_COLLECT",
    "customer": { "vpa": "insufficient@riskpay" }
  }'

# 2. Drive it to its terminal state. In TEST mode the outcome is decided
#    by the VPA above — no request body needed.
curl -X POST https://api.riskpay.in/v1/payments/create/pay_R7KQ2M4X9B1T/capture \\
  -H "Authorization: Bearer sk_test_…" \\
  -H "Idempotency-Key: $(uuidgen)"

# The matching webhook fires within a few seconds. Point your endpoint at a
# tunnel and verify the signature end to end before you go live.`}
              />
            }
          >
            <p>
              UPI outcomes are selected the same way, by the payer VPA. A successful sandbox capture issues a synthetic
              12-digit RRN in the same shape NPCI returns, so your reconciliation code can be exercised properly.
            </p>

            <div className="panel overflow-hidden">
              <table className="table-riskpay">
                <thead>
                  <tr>
                    <th>VPA</th>
                    <th>Outcome</th>
                    <th>Behaviour</th>
                  </tr>
                </thead>
                <tbody>
                  {TEST_VPAS.map((vpa) => (
                    <tr key={vpa.vpa}>
                      <td className="numeral whitespace-nowrap text-xs">{vpa.vpa}</td>
                      <td>
                        <span
                          className={`badge ${
                            vpa.outcome === 'SUCCESS'
                              ? 'badge-success'
                              : vpa.outcome === 'PENDING'
                                ? 'badge-warning'
                                : 'badge-danger'
                          }`}
                        >
                          {vpa.failureCode ?? vpa.outcome}
                        </span>
                      </td>
                      <td className="text-xs leading-relaxed text-content-muted">{vpa.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* ---- Payment routes ---------------------------------------- */}
          <Section
            id="route-overview"
            eyebrow="Payment routes"
            title="How routing works"
            aside={
              <CodePanel
                title="Selection order"
                source={`1. eligible     enabled · right kind and mode
                amount inside the route's band
                under its concurrency cap
                under its daily cap
                not marked DOWN

2. least busy   fewest payments in flight

3. least recent used longest ago, so consecutive
                payments alternate between providers

4. priority     the operator's thumb on the scale,
                for ties only`}
              />
            }
          >
            <p>
              A <em>route</em> is one upstream endpoint a payment can be handed to. There are normally several of
              each kind, because a single provider is a single point of failure and most high-risk acquirers cap how
              much they will take at once.
            </p>
            <p>
              Two kinds exist. An <strong className="text-content">intent route</strong> returns a{' '}
              <code className="numeral text-content-muted">upi://pay</code> deep link. A{' '}
              <strong className="text-content">checkout route</strong> returns a hosted payment page URL. Each has a
              unique name and code, and each can be switched on or off from the admin panel without a deploy.
            </p>
            <p>
              Selection happens inside a database transaction that locks the chosen row, so two payments arriving in
              the same millisecond cannot both be told the same idle route is idle. If the chosen route refuses or
              times out, the next eligible one is tried — up to three — before the request fails.
            </p>
            <Callout>
              <p>
                A route holds its slot until the <em>payment</em> reaches a terminal state, not until the HTTP call
                returns. A provider that has handed out a link and is waiting on the payer is genuinely still
                occupied, and treating it as free is how one provider ends up carrying everything.
              </p>
            </Callout>
            <p className="text-xs text-content-subtle">
              Everything below describes what a provider has to implement to become a route. Merchants never see
              routes and never choose one.
            </p>
          </Section>

          <Section
            id="route-intent-contract"
            eyebrow="Payment routes"
            title="Intent route contract"
            aside={
              <>
                <CodePanel
                  title="Request we send"
                  json
                  source={`POST https://psp.example.com/collect
Content-Type: application/json
X-RiskPay-Timestamp: 1788013762
X-RiskPay-Signature: v1=8f3c…

{
  "route": "acquirer-north-upi",
  "kind": "UPI_INTENT",
  "mode": "LIVE",
  "reference": "pay_R7KQ2M4X9B1T",
  "amount": "1499.50",
  "amount": "1499.50",
  "currency": "INR",
  "merchant_name": "Northwind Retail",
  "merchant_ref": "RP_MERCH_8F2K10",
  "order_id": "order_4821",
  "note": "Order #4821",
  "customer_name": "Asha Menon",
  "customer_email": "asha@example.com",
  "customer_phone": "9876543210",
  "callback_url":
    "https://api.riskpay.in/api/v1/routes/acquirer-north-upi/callback",
  "timestamp": 1788013762
}`}
                />
                <CodePanel
                  title="Reply we expect · 200"
                  json
                  source={`{
  "status": "success",
  "reference": "PSP-88213related",
  "intent": "upi://pay?pa=acq%40hdfcbank&pn=Northwind…",
  "vpa": "acq@hdfcbank",
  "expires_in": 900,
  "message": "Intent issued."
}`}
                />
              </>
            }
          >
            <p>
              We <code className="numeral text-content-muted">POST</code> JSON to the endpoint configured on the
              route and expect a JSON reply within its timeout, which defaults to fifteen seconds.
            </p>

            <div className="panel overflow-hidden">
              <table className="table-riskpay">
                <thead>
                  <tr>
                    <th className="w-44">We send</th>
                    <th className="w-28">Type</th>
                    <th>Meaning</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['route', 'string', 'The route code. Lets one endpoint serve several configured routes.'],
                    ['kind', 'enum', 'UPI_INTENT or CHECKOUT.'],
                    ['mode', 'enum', 'TEST or LIVE. A route only ever receives payments of its own mode.'],
                    ['reference', 'string', 'Our payment id. Quote it back on the callback as gateway_reference.'],
                    ['amount', 'string', 'Rupees, always with exactly two decimals: "1499.50".'],
                    ['currency', 'string', 'ISO 4217. Always INR today.'],
                    ['merchant_name', 'string', 'Display name. Show it in the payer app as the payee.'],
                    ['merchant_ref', 'string', 'Our merchant identifier, for your own reconciliation.'],
                    ['order_id', 'string | null', "The merchant's own order reference, when they sent one."],
                    ['note', 'string | null', 'Payment note. UPI truncates this to 50 characters.'],
                    ['customer_name / _email / _phone', 'string | null', 'Present when the merchant supplied them.'],
                    ['callback_url', 'string', 'Where you report the outcome. Unique per route.'],
                    ['timestamp', 'integer', 'Unix seconds at which we built the request.'],
                  ].map(([name, type, meaning]) => (
                    <tr key={name}>
                      <td className="align-top">
                        <span className="numeral text-xs text-content">{name}</span>
                      </td>
                      <td className="align-top">
                        <span className="numeral text-xs text-content-subtle">{type}</span>
                      </td>
                      <td className="align-top text-xs leading-relaxed text-content-muted">{meaning}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p>
              Field names can be remapped per route, so a provider that calls the amount{' '}
              <code className="numeral text-content-muted">txn_amount</code> needs no code on our side — only a
              mapping in the admin panel. The same applies to reading the reply.
            </p>

            <div className="panel overflow-hidden">
              <table className="table-riskpay">
                <thead>
                  <tr>
                    <th className="w-44">You return</th>
                    <th className="w-28">Required</th>
                    <th>Meaning</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['status', 'yes', 'success, ok or true to accept. Anything else is read as a refusal and we move to the next route.'],
                    ['intent', 'one of these two', 'A complete upi://pay?… URI. We build the per-app links and the QR from it.'],
                    ['vpa', 'one of these two', 'Your payee VPA. If you send this instead of intent, we build the intent ourselves.'],
                    ['reference', 'recommended', 'Your own identifier. Stored against the payment and used to match your callback.'],
                    ['expires_in', 'no', 'Seconds the intent stays valid. We assume 900 when absent.'],
                    ['failure_code', 'no', 'Short machine code when refusing. Shown to the operator in the attempt log.'],
                    ['message', 'no', 'Human-readable detail. Also shown in the attempt log.'],
                  ].map(([name, required, meaning]) => (
                    <tr key={name}>
                      <td className="align-top">
                        <span className="numeral text-xs text-content">{name}</span>
                      </td>
                      <td className="align-top">
                        <span
                          className={`badge ${
                            required === 'yes' ? 'badge-success' : required === 'no' ? 'badge-neutral' : 'badge-info'
                          }`}
                        >
                          {required}
                        </span>
                      </td>
                      <td className="align-top text-xs leading-relaxed text-content-muted">{meaning}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Callout tone="warning" icon={AlertTriangle}>
              <p>
                A reply that says <code className="numeral">success</code> but carries neither{' '}
                <code className="numeral">intent</code> nor <code className="numeral">vpa</code> is treated as a
                failure and counts against the route's health. There is nothing to hand the payer, so calling it a
                success only hides the problem until the complaints arrive.
              </p>
            </Callout>
          </Section>

          <Section
            id="route-checkout-contract"
            eyebrow="Payment routes"
            title="Checkout route contract"
            aside={
              <>
                <CodePanel
                  title="Request we send"
                  json
                  source={`POST https://psp.example.com/page
Content-Type: application/json

{
  "route": "acquirer-north-page",
  "kind": "CHECKOUT",
  "mode": "LIVE",
  "reference": "pay_R7KQ2M4X9B1T",
  "amount": "1299.00",
  "amount": "1299.00",
  "currency": "INR",
  "merchant_name": "Northwind Retail",
  "merchant_ref": "RP_MERCH_8F2K10",
  "order_id": "order_4821",
  "customer_email": "asha@example.com",
  "return_url":
    "https://api.riskpay.in/api/v1/checkout/9f2c…/return",
  "callback_url":
    "https://api.riskpay.in/api/v1/routes/acquirer-north-page/callback",
  "timestamp": 1788013762
}`}
                />
                <CodePanel
                  title="Reply we expect · 200"
                  json
                  source={`{
  "status": "success",
  "reference": "PSP-88213",
  "payment_url": "https://psp.example.com/pay/abc123",
  "expires_in": 900
}`}
                />
              </>
            }
          >
            <p>
              Identical to the intent contract with two differences: the request carries a{' '}
              <code className="numeral text-content-muted">return_url</code>, and the reply must carry a{' '}
              <code className="numeral text-content-muted">payment_url</code>.
            </p>
            <p>
              <code className="numeral text-content-muted">return_url</code> points back at us, not at the merchant.
              Send the customer's browser there when your page finishes, whatever the outcome; we read the state your
              callback has already recorded and forward them to the merchant with a signed result. Sending them
              straight to the merchant instead skips that signature and the merchant has no way to trust what
              arrives.
            </p>
            <p>
              If your endpoint can also produce a UPI intent, return it alongside{' '}
              <code className="numeral text-content-muted">payment_url</code> as{' '}
              <code className="numeral text-content-muted">intent</code>. We will render it on our own page rather
              than bouncing the customer to another site, which measurably fewer of them abandon.
            </p>

            <Callout>
              <p>
                <code className="numeral">payment_url</code> must be reachable by a member of the public on a phone,
                over HTTPS, with no session from our side. It is opened directly in the customer's browser.
              </p>
            </Callout>
          </Section>

          <Section
            id="route-callback"
            eyebrow="Payment routes"
            title="Reporting the outcome"
            aside={
              <>
                <CodePanel
                  title="Callback you send us"
                  json
                  source={`POST https://api.riskpay.in/api/v1/routes/{route-code}/callback
Content-Type: application/json
X-RiskPay-Timestamp: 1788013801
X-RiskPay-Signature: v1=<hmac>

{
  "reference": "PSP-88213",
  "gateway_reference": "pay_R7KQ2M4X9B1T",
  "status": "success",
  "rrn": "612845009182",
  "payment_channel": "gpay",
  "failure_code": null,
  "message": "Approved"
}`}
                />
                <CodePanel
                  title="Our reply"
                  json
                  source={`{
  "received": true,
  "payment_id": "pay_R7KQ2M4X9B1T",
  "status": "success"
}`}
                />
              </>
            }
          >
            <p>
              The callback is the only thing that moves money. Neither the reply to your original call nor the
              browser coming back through <code className="numeral text-content-muted">return_url</code> is treated
              as an outcome — a browser can be closed, and a redirect can be typed by hand.
            </p>

            <div className="panel overflow-hidden">
              <table className="table-riskpay">
                <thead>
                  <tr>
                    <th className="w-44">Field</th>
                    <th className="w-28">Required</th>
                    <th>Meaning</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['status', 'yes', 'success / paid / captured to credit. failed / declined to fail. pending is acknowledged and changes nothing.'],
                    ['gateway_reference', 'one of these two', 'Our pay_… reference from the original request.'],
                    ['reference', 'one of these two', 'Your identifier, as returned on the original call. We match on either.'],
                    ['rrn or utr', 'no', 'The NPCI retrieval reference. Twelve digits. Stored on the payment and shown to the merchant.'],
                    ['payment_channel', 'no', 'Which app or bank the payer used: gpay, phonepe, hdfc…'],
                    ['failure_code', 'no', 'Short machine code on failure.'],
                    ['message', 'no', 'Human detail on failure. Reaches the merchant.'],
                  ].map(([name, required, meaning]) => (
                    <tr key={name}>
                      <td className="align-top">
                        <span className="numeral text-xs text-content">{name}</span>
                      </td>
                      <td className="align-top">
                        <span
                          className={`badge ${
                            required === 'yes' ? 'badge-success' : required === 'no' ? 'badge-neutral' : 'badge-info'
                          }`}
                        >
                          {required}
                        </span>
                      </td>
                      <td className="align-top text-xs leading-relaxed text-content-muted">{meaning}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p>
              Callbacks are idempotent. Repeating one for a payment already in a terminal state returns the existing
              state and does nothing else, so retrying after a timeout is always safe.
            </p>

            <Callout tone="warning" icon={AlertTriangle}>
              <p>
                When a signing secret is configured on the route, both directions are signed:{' '}
                <code className="numeral">HMAC-SHA256</code> over{' '}
                <code className="numeral">{'`${timestamp}.${rawBody}`'}</code>, sent as{' '}
                <code className="numeral">X-RiskPay-Signature: v1=…</code>. Callbacks older than five minutes or
                carrying a bad signature are rejected outright, not queued.
              </p>
            </Callout>
          </Section>

          <Section
            id="return-url"
            eyebrow="Payment routes"
            title="Verifying a return URL"
            aside={
              <CodePanel
                title="Node.js"
                source={`import crypto from 'node:crypto';

// From Developer Settings → Return-URL signing key.
const KEY = process.env.RISKPAY_RETURN_KEY;

export function verifyReturn(query) {
  const { signature, ...rest } = query;

  const payload = Object.keys(rest)
    .sort()
    .map((k) => \`\${k}=\${rest[k]}\`)
    .join('&');

  const expected = crypto
    .createHmac('sha256', KEY)
    .update(payload)
    .digest('hex');

  const ok = crypto.timingSafeEqual(
    Buffer.from(expected),
    Buffer.from(String(signature)),
  );

  // Signed, recent, and still only a hint — the webhook is the record.
  return ok && Date.now() / 1000 - Number(rest.timestamp) < 900;
}`}
              />
            }
          >
            <p>
              When hosted checkout finishes, the customer is sent to your{' '}
              <code className="numeral text-content-muted">return_url</code> with the result in the query string:
            </p>
            <CodePanel
              title="Redirect"
              source={`https://yourshop.example/orders/4821/return
  ?session_id=cs_8H2KQ4M1XB7T
  &payment_id=pay_R7KQ2M4X9B1T
  &order_id=order_4821
  &amount=1299.00
  &currency=INR
  &status=paid
  &timestamp=1788013801
  &signature=42db9b22…`}
            />
            <p>
              The signature is <code className="numeral text-content-muted">HMAC-SHA256</code> over every parameter
              except <code className="numeral text-content-muted">signature</code> itself, sorted by name and joined
              as <code className="numeral text-content-muted">k=v</code> with{' '}
              <code className="numeral text-content-muted">&amp;</code>. The key is derived per merchant and shown in
              Developer Settings.
            </p>
            <Callout tone="warning" icon={AlertTriangle}>
              <p>
                Verify it before you show a receipt, and treat it as a hint rather than a record. A customer whose
                phone dies after paying never loads this URL at all — the{' '}
                <code className="numeral">payment.success</code> webhook is what you fulfil orders from.
              </p>
            </Callout>
          </Section>

          {/* ---- Webhooks ---------------------------------------------- */}
          <Section
            id="webhook-events"
            eyebrow="Webhooks"
            title="Events"
            aside={
              <>
                <CodePanel
                  title="Envelope"
                  json
                  source={`{
  "id": "3b1c9c2e-6f4a-4d78-9c11-7a2e5b0d4f83",
  "type": "payment.success",
  "created_at": "2026-08-28T09:16:04.902Z",
  "mode": "LIVE",
  "data": { }
}`}
                />
                <CodePanel title="payment.success · data" json source={WEBHOOK_EVENTS[0]!.payload} />
              </>
            }
          >
            <p>
              Webhooks are the authoritative signal that a payment completed. A payer can approve a UPI collect long
              after your checkout page has closed, so an order must never be fulfilled on a client-side redirect alone.
            </p>
            <p>
              Every delivery is an envelope with a stable <code className="font-mono text-content">id</code>. Retries
              reuse that id, so deduplicating on it makes your handler safely idempotent. Failed deliveries retry six
              times with exponential backoff — roughly 10 seconds, 40 seconds, 3 minutes, 11 minutes, 42 minutes, then
              nearly 3 hours. Acknowledge with a 2xx within 8 seconds and do the real work on a queue.
            </p>

            <div className="panel overflow-hidden">
              <table className="table-riskpay">
                <thead>
                  <tr>
                    <th className="w-48">Event</th>
                    <th>Fires when</th>
                  </tr>
                </thead>
                <tbody>
                  {WEBHOOK_EVENTS.map((event) => (
                    <tr key={event.type}>
                      <td className="numeral align-top text-xs text-content">{event.type}</td>
                      <td className="text-xs leading-relaxed text-content-muted">{event.when}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-xs text-content-subtle">
              An endpoint that fails 50 consecutive deliveries is disabled automatically. Re-enable it from Developer
              Settings in the merchant portal once the receiver is healthy.
            </p>
          </Section>

          <Section
            id="webhook-signatures"
            eyebrow="Webhooks"
            title="Signature verification"
            aside={
              <LanguageTabs
                snippets={VERIFICATION_SNIPPETS}
                language={language}
                onLanguageChange={setLanguage}
                title="Verify"
              />
            }
          >
            <p>Every delivery carries a signature header:</p>

            <CodePanel
              title="Header"
              source={`RiskPay-Signature: t=1756377600,v1=8f3c1e5d7a2b9046c8e1f3a5b7d9c0e2f4a6b8d0c2e4f6a8b0d2c4e6f8a0c0b2
RiskPay-Event-Id: 3b1c9c2e-6f4a-4d78-9c11-7a2e5b0d4f83
RiskPay-Event-Type: payment.success`}
            />

            <p>
              The signed string is <code className="font-mono text-content">{'<timestamp>.<raw request body>'}</code>,
              and <code className="font-mono text-content">v1</code> is its HMAC-SHA256 digest under your endpoint
              secret (<code className="font-mono text-content">whsec_…</code>), hex encoded.
            </p>

            <p>Three rules, in the order they are most often got wrong:</p>
            <ol className="ml-4 list-decimal space-y-2 marker:text-content-faint">
              <li>
                <strong className="font-medium text-content">Sign the raw bytes.</strong> Parsing the JSON and
                re-serialising it reorders keys and changes whitespace, and the digest will never match. In Express that
                means <code className="font-mono text-content">express.raw()</code> on this route, not{' '}
                <code className="font-mono text-content">express.json()</code>.
              </li>
              <li>
                <strong className="font-medium text-content">Enforce the timestamp window.</strong> Reject anything more
                than 300 seconds from now. Because the timestamp is inside the MAC, an attacker cannot re-stamp a
                captured request — this is what makes the scheme replay-resistant.
              </li>
              <li>
                <strong className="font-medium text-content">Compare in constant time.</strong> Use{' '}
                <code className="font-mono text-content">timingSafeEqual</code>,{' '}
                <code className="font-mono text-content">hmac.compare_digest</code> or{' '}
                <code className="font-mono text-content">hash_equals</code>. A plain string comparison leaks the digest
                one byte at a time.
              </li>
            </ol>

            <Callout>
              <p>
                Rotating a secret from Developer Settings returns the new value once and takes effect on the next event.
                Deploy the new secret to your receiver before you rotate, and accept either value during the changeover.
              </p>
            </Callout>
          </Section>

          {/* ---- Errors ------------------------------------------------ */}
          <Section
            id="errors"
            eyebrow="Reference"
            title="Error codes"
            aside={
              <>
                <CodePanel
                  title="Error shape"
                  json
                  source={`{
  "error": {
    "type": "invalid_request_error",
    "code": "amount_out_of_range",
    "message": "Amount must be between ₹1.00 and ₹2,00,000.00.",
    "param": "amount",
    "hint": "Check your configured per-transaction limits.",
    "request_id": "c3a91f0e-7b21-4d64-9f88-0e4b2a1c7d55"
  }
}`}
                />
                <Callout>
                  <p>
                    Branch on <code className="font-mono text-content">error.code</code>, never on{' '}
                    <code className="font-mono text-content">error.message</code>. Codes are a stable contract; messages
                    are written for humans and may be reworded at any time.
                  </p>
                </Callout>
              </>
            }
          >
            <p>
              Errors are returned with a conventional HTTP status and a machine-readable body. 4xx means the request
              needs changing; 5xx and 503 mean the request was fine and is safe to retry with the same Idempotency-Key.
            </p>

            <div className="panel overflow-hidden">
              <div className="scrollbar-thin max-h-[640px] overflow-auto">
                <table className="table-riskpay">
                  <thead>
                    <tr>
                      <th className="w-16">Status</th>
                      <th className="w-56">Code</th>
                      <th>Meaning &amp; what to do</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ERROR_CODES.map((row) => (
                      <tr key={row.code + row.status}>
                        <td className="align-top">
                          <span
                            className={`badge ${
                              row.status < 400
                                ? 'badge-success'
                                : row.status < 500
                                  ? 'badge-warning'
                                  : 'badge-danger'
                            }`}
                          >
                            {row.status}
                          </span>
                        </td>
                        <td className="align-top">
                          <span className="numeral text-xs text-content">{row.code}</span>
                          <span className="mt-1 block text-2xs text-content-faint">{row.type}</span>
                        </td>
                        <td className="align-top text-xs leading-relaxed text-content-muted">
                          {row.message}
                          <span className="mt-1 block text-content-subtle">{row.action}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Section>

          <footer className="flex flex-wrap items-center justify-between gap-4 py-10 text-xs text-content-faint">
            <p>© {new Date().getFullYear()} RiskPay Technologies Private Limited.</p>
            <div className="flex gap-5">
              <a href={portals.landing} className="inline-flex items-center gap-1 hover:text-content-muted">
                riskpay.in
                <ExternalLink className="h-3 w-3" aria-hidden="true" />
              </a>
              <a
                href={portals.merchant}
                className="inline-flex items-center gap-1 hover:text-content-muted"
              >
                Merchant portal
                <ExternalLink className="h-3 w-3" aria-hidden="true" />
              </a>
              <a href="mailto:support@riskpay.in" className="hover:text-content-muted">
                support@riskpay.in
              </a>
            </div>
          </footer>
        </div>
      </main>
    </div>
  );
}
